import React, { useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate, useLocation, useParams } from 'react-router-dom';
import { 
  selectUser,
  selectIsUserLoggedIn,
  selectIsAdminLoggedIn,
  selectCurrentLanguage,
  selectUserAssetBalances,
  selectAssetTransactions,
  fetchUserAssetBalances,
  fetchAssetTransactions,
  fetchCartItems,
  fetchWithcookieData,
  logoutUser,
  loginUser,
  checkLoginStatus,
  setCurrentLanguage,
  selectGlobalMenus,
  selectMainMenus
} from '../redux/WithcookieSlice';
import { selectMenus } from '../redux/editorSlice';
import { createExternalLayoutWrapper } from './ExternalLayoutLoader';

/**
 * Hook to create props for external skins from Redux store
 * @param {Object} layoutProps - Additional layout props
 * @returns {import('../types/external-skin').ExternalSkinProps}
 */
export function useExternalSkinProps(layoutProps = {}) {
  console.log('🔍 useExternalSkinProps layoutProps:', layoutProps);
  console.log('🔍 useExternalSkinProps layoutProps.children:', layoutProps.children);
  console.log('🔍 useExternalSkinProps 전체 layoutProps 구조:', Object.keys(layoutProps));
  
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  
  // 🔧 외부 스킨이 접근할 수 있도록 전역 navigate 함수 설정
  useEffect(() => {
    if (typeof window !== 'undefined') {
      window.withcookieNavigate = navigate;
      
      // 🔧 외부 스킨의 링크 클릭을 React Router로 처리
      const handleLinkClick = (e) => {
        const target = e.target.closest('a');
        if (target && target.href) {
          const url = new URL(target.href);
          // 같은 도메인의 링크만 처리
          if (url.origin === window.location.origin) {
            e.preventDefault();
            const path = url.pathname + url.search + url.hash;
            console.log('🔗 링크 클릭 감지:', path);
            navigate(path);
          }
        }
      };
      
      document.addEventListener('click', handleLinkClick);
      
      return () => {
        document.removeEventListener('click', handleLinkClick);
      };
    }
  }, [navigate]);
  
  // Select data from Redux store
  const user = useSelector(selectUser);
  const withcookieData = useSelector(state => state.withcookie?.withcookieData || {});
  const company = withcookieData?.skin?.company || null;
  
  // 프리뷰에서 전달된 메뉴 데이터 우선 사용, 없으면 Redux에서 가져오기
  const reduxMenus = useSelector(selectMenus);
  const reduxGlobalMenus = useSelector(selectGlobalMenus);
  const reduxMainMenus = useSelector(selectMainMenus);
  
  // 프리뷰 데이터에서 메뉴 정보 추출 (layoutProps.editorData 또는 layoutProps.previewData)
  const previewMenus = layoutProps.editorData?.menus || layoutProps.previewData?.menus || [];
  const previewGlobalMenus = layoutProps.globalMenus || [];
  const previewMainMenus = layoutProps.mainMenus || [];
  
  // 최종 메뉴 데이터 결정 (프리뷰 데이터 우선, 없거나 비어있으면 Redux 데이터)
  const menus = previewMenus.length > 0 ? previewMenus : reduxMenus;
  const globalMenus = previewGlobalMenus.length > 0 
    ? previewGlobalMenus 
    : (reduxGlobalMenus.length > 0 ? reduxGlobalMenus : previewMenus.filter(menu => menu.type === 'GLOBAL'));
  const mainMenus = previewMainMenus.length > 0 
    ? previewMainMenus 
    : (reduxMainMenus.length > 0 ? reduxMainMenus : previewMenus.filter(menu => menu.type === 'MAIN' || !menu.type));
  
  console.log('🔍 ExternalLayoutWrapper 메뉴 데이터 소스 확인:');
  console.log('  - previewMenus 개수:', previewMenus.length);
  console.log('  - previewMenus 내용:', previewMenus);
  console.log('  - reduxMenus 개수:', reduxMenus.length);
  console.log('  - reduxGlobalMenus 개수:', reduxGlobalMenus.length);
  console.log('  - reduxMainMenus 개수:', reduxMainMenus.length);
  console.log('  - reduxGlobalMenus 내용:', reduxGlobalMenus);
  console.log('  - reduxMainMenus 내용:', reduxMainMenus);
  console.log('  - previewGlobalMenus 개수:', previewGlobalMenus.length);
  console.log('  - previewMainMenus 개수:', previewMainMenus.length);
  console.log('  - 현재 URL:', window.location.pathname);
  console.log('  - 현재 모드:', window.location.pathname.includes('/preview') ? 'PREVIEW' : 'EDITOR');
  console.log('  - 최종 사용할 menus 개수:', menus.length);
  console.log('  - 최종 사용할 globalMenus 개수:', globalMenus.length);
  console.log('  - 최종 사용할 mainMenus 개수:', mainMenus.length);
  console.log('  - 최종 globalMenus 내용:', globalMenus);
  console.log('  - 최종 mainMenus 내용:', mainMenus);
  const assetBalances = useSelector(selectUserAssetBalances);
  const assetTransactions = useSelector(selectAssetTransactions);
  const cartItems = useSelector(state => state.withcookie?.cartItems || []);
  const isUserLoggedIn = useSelector(selectIsUserLoggedIn);
  const isAdminLoggedIn = useSelector(selectIsAdminLoggedIn);
  const currentLanguage = useSelector(selectCurrentLanguage);
  const translations = useSelector(state => state.withcookie?.translationDictionary || {});
  
  // Translation function
  const t = (key, params = {}) => {
    let translation = translations[key] || key;
    
    // Replace parameters in translation
    Object.entries(params).forEach(([param, value]) => {
      translation = translation.replace(`{${param}}`, value);
    });
    
    return translation;
  };
  
  // Currency formatter
  const formatCurrency = (amount, currency = 'KRW') => {
    return new Intl.NumberFormat('ko-KR', {
      style: 'currency',
      currency: currency
    }).format(amount);
  };
  
  // Date formatter
  const formatDate = (date, format = 'YYYY-MM-DD') => {
    const d = new Date(date);
    if (isNaN(d.getTime())) return date;
    
    // Simple date formatting
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    const hours = String(d.getHours()).padStart(2, '0');
    const minutes = String(d.getMinutes()).padStart(2, '0');
    
    return format
      .replace('YYYY', year)
      .replace('MM', month)
      .replace('DD', day)
      .replace('HH', hours)
      .replace('mm', minutes);
  };
  
  // Extract theme from withcookieData
  const theme = withcookieData?.skin?.theme?.colorset || {};
  
  // 메뉴 데이터 통합 및 변환
  const convertMenusToNavigation = () => {
    
    let allMenus = [];
    
    // 🔧 중복 방지: menus가 이미 전체 메뉴를 포함하고 있다면 그것만 사용
    if (Array.isArray(menus) && menus.length > 0) {
      // menus에 이미 type이 있는 완전한 메뉴 데이터가 있으면 그것만 사용
      const hasTypedMenus = menus.some(menu => menu.type === 'GLOBAL' || menu.type === 'MAIN');
      
      if (hasTypedMenus) {
        allMenus = menus.map(menu => ({
          ...menu,
          source: 'preview'
        }));
      } else {
        
        // 1. editorSlice의 menus 추가 (웹빌더에서 만든 메뉴)
        if (Array.isArray(menus)) {
          allMenus.push(...menus.map(menu => ({
            ...menu,
            source: 'editor'
          })));
        }
        
        // 2. globalMenus 추가 (전역 메뉴) - menus와 중복되지 않는 경우만
        if (Array.isArray(globalMenus) && globalMenus.length > 0) {
          const nonDuplicateGlobalMenus = globalMenus.filter(gMenu => 
            !menus.some(menu => menu.id === gMenu.id)
          );
          allMenus.push(...nonDuplicateGlobalMenus.map(menu => ({
            ...menu,
            source: 'global'
          })));
        }
        
        // 3. mainMenus 추가 (메인 메뉴) - menus와 중복되지 않는 경우만
        if (Array.isArray(mainMenus) && mainMenus.length > 0) {
          const nonDuplicateMainMenus = mainMenus.filter(mMenu => 
            !menus.some(menu => menu.id === mMenu.id)
          );
          allMenus.push(...nonDuplicateMainMenus.map(menu => ({
            ...menu,
            source: 'main'
          })));
        }
      }
    } else {
      
      if (Array.isArray(globalMenus)) {
        allMenus.push(...globalMenus.map(menu => ({
          ...menu,
          source: 'global'
        })));
      }
      
      if (Array.isArray(mainMenus)) {
        allMenus.push(...mainMenus.map(menu => ({
          ...menu,
          source: 'main'
        })));
      }
    }
    
    // 외부 스킨용 NavigationItem 구조로 변환
    const convertToNavigationItem = (menu) => {
      const menuPath = menu.url || menu.path || menu.link || '#';
      
      return {
        label: menu.name || menu.label || menu.title || 'Untitled',
        path: menuPath,
        icon: menu.icon,
        id: menu.id,
        parentId: menu.parentId,
        order: menu.order || 0,
        isVisible: menu.isVisible !== false,
        requireLogin: menu.requireLogin || false,
        source: menu.source,
        // 🔧 navigate 함수를 각 메뉴 아이템에 직접 추가
        onClick: (e) => {
          if (e) {
            e.preventDefault();
          }
          if (menuPath && menuPath !== '#') {
            console.log('🔗 메뉴 클릭:', menu.name, '→', menuPath);
            navigate(menuPath);
          }
        },
        // 🔧 전역 navigate 함수 직접 설정
        navigateFunction: navigate
      };
    };
    
    // 메뉴를 NavigationItem으로 변환
    const navigationItems = allMenus.map(convertToNavigationItem);
    
    // 로그인 상태에 따른 메뉴 필터링
    const filteredItems = navigationItems.filter(item => {
      if (item.requireLogin && !isUserLoggedIn) {
        return false;
      }
      return item.isVisible;
    });
    
    // 계층 구조 구성 (부모-자식 관계)
    const buildHierarchy = (items) => {
      const itemMap = new Map();
      const rootItems = [];
      
      // 모든 아이템을 맵에 저장
      items.forEach(item => {
        itemMap.set(item.id, { ...item, children: [] });
      });
      
      // 계층 구조 구성
      items.forEach(item => {
        const menuItem = itemMap.get(item.id);
        if (item.parentId && itemMap.has(item.parentId)) {
          // 부모가 있는 경우 부모의 children에 추가
          const parent = itemMap.get(item.parentId);
          parent.children.push(menuItem);
        } else {
          // 최상위 메뉴
          rootItems.push(menuItem);
        }
      });
      
      // 순서대로 정렬
      const sortByOrder = (items) => {
        items.sort((a, b) => (a.order || 0) - (b.order || 0));
        items.forEach(item => {
          if (item.children && item.children.length > 0) {
            sortByOrder(item.children);
          }
        });
      };
      
      sortByOrder(rootItems);
      return rootItems;
    };
    
    const hierarchicalNavigation = buildHierarchy(filteredItems);
    
    console.log('✅ ExternalLayoutWrapper 최종 navigation 데이터:');
    console.log('  - 계층 구조 navigation 개수:', hierarchicalNavigation.length);
    console.log('  - 계층 구조 navigation 내용:', hierarchicalNavigation);
    console.log('  - 각 메뉴 항목 상세:', hierarchicalNavigation.map(item => ({
      label: item.label,
      path: item.path,
      source: item.source,
      children: item.children?.length || 0
    })));
    
    return hierarchicalNavigation;
  };
  
  const navigation = convertMenusToNavigation();
  
  // Create the props object
  console.log('🔍 useExternalSkinProps creating skinProps with children:', layoutProps.children);
  
  const skinProps = {
    // Data props
    data: {
      user,
      company,
      withcookieData,
      navigation, // 외부 스킨용 통합 메뉴 데이터
      menus,
      globalMenus,
      mainMenus,
      assetBalances,
      transactions: assetTransactions,
      cartItems,
      isUserLoggedIn,
      isAdminLoggedIn,
      currentLanguage,
      translations,
      // 🔧 외부 스킨이 사용할 수 있는 navigation 헬퍼 추가
      navigateHandler: navigate
    },
    
    // Action props
    actions: {
      onLogin: async (credentials) => {
        return dispatch(loginUser(credentials));
      },
      onLogout: () => {
        dispatch(logoutUser());
      },
      onCheckAuth: async () => {
        return dispatch(checkLoginStatus());
      },
      onFetchBalances: async () => {
        return dispatch(fetchUserAssetBalances());
      },
      onFetchTransactions: async (params) => {
        return dispatch(fetchAssetTransactions(params));
      },
      onFetchCartItems: async () => {
        return dispatch(fetchCartItems());
      },
      onFetchMenus: async () => {
        // Menus are typically loaded from editorSlice
        return Promise.resolve();
      },
      onFetchWithcookieData: async () => {
        return dispatch(fetchWithcookieData());
      },
      onUpdateCart: (item) => {
        // Cart update logic would go here
        console.log('Update cart:', item);
      },
      onChangeLanguage: (lang) => {
        dispatch(setCurrentLanguage(lang));
      }
    },
    
    // Utility props
    utils: {
      navigate,
      location: {
        pathname: location.pathname,
        search: location.search,
        hash: location.hash,
        state: location.state
      },
      params,
      t,
      formatCurrency,
      formatDate
    },
    
    // Layout props
    layout: {
      ...layoutProps,
      children: layoutProps.children, // children 명시적으로 추가
      currentMenuId: layoutProps.currentMenuId,
      breadcrumbs: layoutProps.breadcrumbs || [],
      pageTitle: layoutProps.pageTitle,
      showHeader: layoutProps.showHeader !== false,
      showFooter: layoutProps.showFooter !== false,
      showSidebar: layoutProps.showSidebar || false
    },
    
    // Theme configuration
    theme: {
      primaryColor: theme.primary || '#181B42',
      secondaryColor: theme.secondary || '#313AB9',
      dangerColor: theme.danger || '#FF2058',
      successColor: theme.success || '#4CAF50',
      warningColor: theme.warning || '#FF9800',
      infoColor: theme.info || '#2196F3',
      darkColor: theme.dark || '#212529',
      lightColor: theme.light || '#F8F9FA',
      fontFamily: theme.fontFamily || "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      fontSize: theme.fontSize || '15px',
      borderRadius: theme.borderRadius || '8px',
      spacing: {
        xs: '0.25rem',
        sm: '0.5rem',
        md: '1rem',
        lg: '1.5rem',
        xl: '2rem'
      },
      breakpoints: {
        sm: '576px',
        md: '768px',
        lg: '992px',
        xl: '1200px'
      }
    }
  };
  
  // children을 최상위 레벨에도 추가
  skinProps.children = layoutProps.children;
  
  console.log('🔍 useExternalSkinProps final skinProps.children:', skinProps.children);
  console.log('🔍 useExternalSkinProps final skinProps.layout.children:', skinProps.layout.children);
  
  return skinProps;
}

/**
 * Wrapper component that provides props to external skins
 * @param {Object} props
 * @param {string} props.layoutId - The external layout ID
 * @param {React.ReactNode} props.children - Child components
 * @param {Object} props.layoutProps - Additional layout props
 */
export function ExternalLayoutProvider({ layoutId, children, ...layoutProps }) {
  console.log('🔍 ExternalLayoutProvider layoutId:', layoutId);
  console.log('🔍 ExternalLayoutProvider children:', children);
  console.log('🔍 ExternalLayoutProvider layoutProps:', Object.keys(layoutProps));
  
  const skinProps = useExternalSkinProps({ ...layoutProps, children });
  
  console.log('🔍 ExternalLayoutProvider skinProps.layout.children:', skinProps.layout.children);
  console.log('🔍 ExternalLayoutProvider skinProps keys:', Object.keys(skinProps));
  
  const LayoutComponent = createExternalLayoutWrapper(layoutId);
  
  return <LayoutComponent {...skinProps} />;
}

export default ExternalLayoutProvider;